import { Group, Indicator, Text } from '@mantine/core';
import {
  IconArrowsSort,
  IconGavel,
  IconPhoneRinging,
  IconReportMoney,
  IconScale,
  IconSortAscending,
  IconSortDescending,
} from '@tabler/icons-react';
import { createColumnHelper } from '@tanstack/react-table';
import { ClientHoverCard } from 'components/ClientHoverCard';
import dayjs from 'dayjs';
import { i18nKeys, tKeysLoose } from 'locales/index';
import { useTranslation } from 'locales/logic';
import Amount from 'shared/components/Amount';
import TpCaseStatusItem from 'shared/components/TpCaseStatusItem';
import type { LightThirdPartyCase } from '../api';

type SortableColumn = 'created_at' | 'updated_at';

type BuildColumnsOptions = {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onToggleSort: (column: SortableColumn) => void;
};

const columnHelper = createColumnHelper<LightThirdPartyCase>();

export const useBuildThirdPartyCaseColumns = ({
  sortBy,
  sortOrder,
  onToggleSort,
}: BuildColumnsOptions) => {
  const { t } = useTranslation();

  const renderSortableHeader = (label: string, column: SortableColumn) => (
    <Group
      gap={4}
      wrap="nowrap"
      onClick={() => onToggleSort(column)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      {label}
      {sortBy === column ? (
        sortOrder === 'asc' ? (
          <IconSortAscending size={14} />
        ) : (
          <IconSortDescending size={14} />
        )
      ) : (
        <IconArrowsSort size={14} />
      )}
    </Group>
  );

  return [
    columnHelper.accessor('case_type', {
      header: t(i18nKeys.CASE_TYPE),
      cell: info => {
        const caseDetail = info.row.original;
        return (
          <Group align="center" wrap="nowrap">
            <Indicator color="red" size={5} disabled={!caseDetail?.conversation?.unread} zIndex={1}>
              {getThirdpartyCaseIcon(caseDetail.case_type)}
            </Indicator>
            <Text size="sm">
              {t(tKeysLoose.THIRD_PARTY_CASES.CASE_TYPE[info.getValue().toUpperCase()])}
            </Text>
          </Group>
        );
      },
    }),
    columnHelper.accessor('case_id', {
      header: t(i18nKeys.FORM.REFERENCE),
      cell: info => (
        <Text size="sm" truncate="end">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('created_at', {
      header: () => renderSortableHeader(t(i18nKeys.CREATION_DATE), 'created_at'),
      cell: info => <Text size="sm">{dayjs(info.getValue()).format('L')}</Text>,
    }),
    columnHelper.accessor('last_update', {
      header: () => renderSortableHeader(t(i18nKeys.LAST_UPDATE), 'updated_at'),
      cell: info => <Text size="sm">{dayjs(info.getValue()).format('L LT')}</Text>,
    }),
    columnHelper.accessor('debtor.fullName', {
      id: 'debtor_name',
      header: t(i18nKeys.COMMON.CLIENT),
      cell: info => {
        const { debtor } = info.row.original;
        return <ClientHoverCard debtorId={debtor.id} debtorName={debtor.fullName} />;
      },
    }),
    columnHelper.accessor(row => (row.closed ? 'closed' : row.started ? 'started' : 'pending'), {
      id: 'status',
      header: t(i18nKeys.STATUS),
      cell: info => <TpCaseStatusItem caseDetail={info.row.original} />,
    }),
    columnHelper.accessor('total_tvac', {
      header: t(i18nKeys.TOTAL_VAT_PRICE),
      cell: info => <Amount value={info.getValue()} />,
    }),
    columnHelper.accessor('total_remaining', {
      header: t(i18nKeys.BALANCE),
      cell: info => <Amount value={info.getValue()} />,
    }),
  ];
};

const getThirdpartyCaseIcon = (caseType: string) => {
  switch (caseType) {
    case 'lawyer':
      return <IconGavel size={20} strokeWidth={1.5} />;
    case 'bailiff':
      return <IconScale size={20} strokeWidth={1.5} />;
    case 'debt_collector':
      return <IconReportMoney size={20} strokeWidth={1.5} />;
    default:
      return <IconPhoneRinging size={20} strokeWidth={1.5} />;
  }
};
